import React from 'react';
import { Chart } from 'primereact/chart';
import { useTranslation } from 'react-i18next';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { CurrencyFormatter, useCurrencyConstants } from '../../../Components/CurrencyFormatter';

const DashboardCharts = (props) => {
    const { t } = useTranslation('common');
    const { currencyFormat, currencySymbol } = useCurrencyConstants();

    const tooltipDescriptionsBtm = [
        t("dashboard.daily_charts"),
        t("dashboard.weekly_charts"),
        t("dashboard.3_months_charts"),
        t("dashboard.6_months_charts"),
        t("dashboard.12_months_charts")
    ];

    const isMonthly = (props.activeIndex === 3 || props.activeIndex === 4 || props.activeIndex === 5) && props.monthlyChart;
    const labelCount = props.collectedPaymentsData?.labels?.length || 0;
    const denseLabels = labelCount > 12;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
            },
        },
        scales: {
            x: {
                grid: {
                    display: !isMonthly,
                },
                ticks: {
                    autoSkip: true,
                    maxRotation: denseLabels ? 45 : 0,
                    minRotation: denseLabels ? 30 : 0,
                    maxTicksLimit: denseLabels ? 6 : 8,
                    font: {
                        size: 10,
                    },
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    maxTicksLimit: 5,
                    font: {
                        size: 10,
                    },
                },
            },
        },
    };

    const renderChart = (chartName, chartData, chartAmount, chartCurrency, tooltipTitle, tooltipDescription, tooltipTargetId) => {
        const activeIndex = props.activeIndex === 3 || props.activeIndex === 4 || props.activeIndex === 5;
        const chartType = activeIndex && props.monthlyChart === true ? 'bar' : 'line';

        return (
            <div className="chart card dashboard-chart-card">
                {!props.loadingChart && (
                    <div className="chart-info">
                        <div className='dashboard-charts'>
                            <div className="dashboard-chart-title">
                                <span className="centerText">{chartName}</span>
                            </div>
                            <div>
                                <Button
                                    id={tooltipTargetId}
                                    type="text"
                                    style={{
                                        fontSize: '18px',
                                        color: 'black',
                                        border: 'none',
                                        background: 'none',
                                    }}
                                    icon="pi pi-question-circle"
                                    className={`p-button-rounded cardSectionsButton`}
                                />
                            </div>
                        </div>
                        <div className="chart-amount">
                            <span className='chart-amount-value'>{chartAmount}</span>
                            <span className="chart-duration">{props.duration}</span>
                        </div>
                        <Tooltip position="bottom" target={`#${tooltipTargetId}`} className="tooltip">
                            <div>
                                <h5 className="title">{tooltipTitle}</h5>
                                <span className="desc">{tooltipDescription}</span>
                                <hr className="hr" />
                                <span className="desc">{tooltipDescriptionsBtm[props.activeIndex - 1]}</span>
                            </div>
                        </Tooltip>
                    </div>
                )}
                <div className="dashboard-chart-canvas">
                    {props.loadingChart
                        ? <ProgressSpinner style={{ display: 'flex', justifyContent: 'center' }} />
                        : <Chart type={chartType} data={chartData} options={chartOptions} />
                    }
                </div>
            </div>
        );
    };

    const collectedAmount = CurrencyFormatter({
        amount: props.paymentScheduleSummaryObject.paidSummary?.amount,
        currencySymbol: currencySymbol,
        currencyFormat: currencyFormat
    });

    const missedAmount = CurrencyFormatter({
        amount: props.paymentScheduleSummaryObject.missedSummary?.amount,
        currencySymbol: currencySymbol,
        currencyFormat: currencyFormat
    });

    const upcomingAmount = CurrencyFormatter({
        amount: props.actualPendingAmount,
        currencySymbol: currencySymbol,
        currencyFormat: currencyFormat
    });

    return (
        <div className="grid p-fluid dashboard-charts-grid">
            <div className="col-12 lg:col-6">
                {renderChart(
                    t('dashboard.collected_payments'),
                    props.collectedPaymentsData,
                    collectedAmount,
                    props.paymentScheduleSummaryObject.paidSummary?.currency,
                    t("dashboard.collected_payments"),
                    t("dashboard.collected_text"),
                    'collected-payments-tooltip'
                )}
                {renderChart(
                    t('dashboard.missed_payments'),
                    props.missedPaymentsData,
                    missedAmount,
                    props.paymentScheduleSummaryObject.missedSummary?.currency,
                    t("dashboard.missed_payments"),
                    t("dashboard.missed_text"),
                    'missed-payments-tooltip'
                )}
                {renderChart(
                    t('dashboard.pending_payments'),
                    props.pendingPaymentsData,
                    upcomingAmount,
                    props.paymentScheduleSummaryObject.missedSummary?.currency,
                    t("dashboard.pending_payments"),
                    t("dashboard.pending_text"),
                    'pending-payments-tooltip'
                )}
            </div>
            <div className="col-12 lg:col-6">
                {renderChart(
                    t('dashboard.new_subscriptions'),
                    props.newSubscriptionsData,
                    props.totalNewSubscriptions,
                    "",
                    t("dashboard.new_subscriptions"),
                    t("dashboard.new_subscriptions_text"),
                    'new-subscriptions-tooltip'
                )}
                {renderChart(
                    t('dashboard.active_subscriptions'),
                    props.activeSubscriptionsData,
                    props.totalActiveSubscriptions,
                    "",
                    t("dashboard.active_subscriptions"),
                    t("dashboard.active_subscriptions_text"),
                    'active-subscriptions-tooltip'
                )}
                {renderChart(
                    t('dashboard.cancelled_subscriptions'),
                    props.cancelledSubscriptionsData,
                    props.totalCancelledSubscriptions,
                    "",
                    t("dashboard.cancelled_subscriptions"),
                    t("dashboard.cancelled_subscriptions_text"),
                    'cancelled-subscriptions-tooltip'
                )}
            </div>
        </div>
    );
};

export default DashboardCharts;
